import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const LotEditorPanel = ({ lots, selectedLot, onLotSelect, onLotUpdate, onLotGroup }) => {
  const [editMode, setEditMode] = useState(false);
  const [groupMode, setGroupMode] = useState(false);
  const [selectedLots, setSelectedLots] = useState([]);
  const [editData, setEditData] = useState({});

  React.useEffect(() => {
    if (selectedLot) {
      setEditData({
        number: selectedLot?.number,
        area: selectedLot?.area,
        dimensions: selectedLot?.dimensions,
        location: selectedLot?.location,
        status: selectedLot?.status || 'available'
      });
    }
  }, [selectedLot]);

  const handleSaveEdit = () => {
    if (selectedLot) {
      onLotUpdate(selectedLot?.number, editData);
      setEditMode(false);
    }
  };

  const handleToggleLotSelection = (lotNumber) => {
    setSelectedLots(prev => 
      prev?.includes(lotNumber)
        ? prev?.filter(n => n !== lotNumber)
        : [...prev, lotNumber]
    );
  };

  const handleGroupLots = () => {
    if (selectedLots?.length >= 2) {
      onLotGroup(selectedLots);
      setSelectedLots([]);
      setGroupMode(false);
    }
  };

  const statusOptions = [
    { value: 'available', label: 'Disponible' },
    { value: 'reserved', label: 'Reservado' },
    { value: 'sold', label: 'Vendido' }
  ];

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Edit3" size={20} className="text-primary" />
          Editor de Lotes
        </h3>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setGroupMode(!groupMode);
              setSelectedLots([]);
            }}
          >
            <Icon name="Combine" size={16} className="mr-1" />
            {groupMode ? 'Cancelar' : 'Agrupar'}
          </Button>
        </div>
      </div>

      {groupMode && (
        <div className="mb-4 p-3 bg-info/10 border border-info rounded-lg">
          <p className="text-xs text-info mb-2 flex items-center gap-2">
            <Icon name="Info" size={14} />
            Selecciona 2 o más lotes para agrupar
          </p>
          {selectedLots?.length >= 2 && (
            <Button
              size="sm"
              onClick={handleGroupLots}
              className="w-full"
            >
              <Icon name="Combine" size={16} className="mr-2" />
              Agrupar {selectedLots?.length} lotes
            </Button>
          )}
        </div>
      )}

      {selectedLot && !groupMode && (
        <div className="mb-4 p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-foreground">Lote {selectedLot?.number}</h4>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditMode(!editMode)}
            >
              <Icon name={editMode ? 'X' : 'Edit2'} size={14} />
            </Button>
          </div>

          {editMode ? (
            <div className="space-y-3">
              <Input
                label="Número de Lote"
                value={editData?.number || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, number: e?.target?.value }))}
              />
              
              <Input
                label="Área (m²)"
                type="number"
                value={editData?.area || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, area: parseFloat(e?.target?.value) }))}
              />
              
              <Input
                label="Dimensiones"
                value={editData?.dimensions || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, dimensions: e?.target?.value }))}
              />
              
              <Input
                label="Ubicación"
                value={editData?.location || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, location: e?.target?.value }))}
              />
              
              <Select
                label="Estado"
                value={editData?.status || 'available'}
                onChange={(e) => setEditData(prev => ({ ...prev, status: e?.target?.value }))}
                options={statusOptions}
              />

              <Button
                onClick={handleSaveEdit}
                className="w-full"
                size="sm"
              >
                <Icon name="Save" size={16} className="mr-2" />
                Guardar Cambios
              </Button>
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Área:</span>
                <span className="font-medium">{selectedLot?.area} m²</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Dimensiones:</span>
                <span className="font-medium">{selectedLot?.dimensions}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Ubicación:</span>
                <span className="font-medium">{selectedLot?.location}</span>
              </div>
              {selectedLot?.isGrouped && (
                <div className="pt-2 border-t border-border">
                  <span className="text-xs text-info flex items-center gap-1">
                    <Icon name="Combine" size={12} />
                    Lote agrupado: {selectedLot?.groupedLots?.join(', ')}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        <h4 className="text-sm font-semibold text-muted-foreground mb-2">
          Todos los Lotes ({lots?.length || 0})
        </h4>
        
        {lots?.map((lot, index) => (
          <div
            key={index}
            onClick={() => {
              if (groupMode) {
                handleToggleLotSelection(lot?.number);
              } else {
                onLotSelect(lot);
              }
            }}
            className={`p-3 rounded-lg border cursor-pointer transition-smooth ${
              groupMode && selectedLots?.includes(lot?.number)
                ? 'border-primary bg-primary/10'
                : selectedLot?.number === lot?.number
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {groupMode && (
                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                    selectedLots?.includes(lot?.number)
                      ? 'border-primary bg-primary' :'border-border'
                  }`}>
                    {selectedLots?.includes(lot?.number) && (
                      <Icon name="Check" size={12} className="text-primary-foreground" />
                    )}
                  </div>
                )}
                <div>
                  <p className="font-semibold text-foreground text-sm">
                    Lote {lot?.number}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {lot?.area} m² • {lot?.location}
                  </p>
                </div>
              </div>
              
              {lot?.isGrouped && (
                <Icon name="Combine" size={16} className="text-info" />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LotEditorPanel;